import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  Groups as GroupsIcon,
  School as SchoolIcon,
  BugReport as BugReportIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../config/axios';
import ReactECharts from 'echarts-for-react';

const SecEventCalendar = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, eventId: null });
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [attendanceStats, setAttendanceStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    event_type: 'cybersecurity',
    target_audience: 'bevoelkerung',
    location: '',
    url: '',
    organizer: '',
    organizer_logo: '',
    show_logo: false,
    is_all_day: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const eventTypes = [
    { value: 'cybersecurity', label: 'Cybersecurity', icon: <SecurityIcon /> },
    { value: 'conference', label: 'Konferenz', icon: <GroupsIcon /> },
    { value: 'training', label: 'Schulung', icon: <SchoolIcon /> },
    { value: 'webinar', label: 'Webinar', icon: <EventIcon /> },
    { value: 'digitale_sprechstunde', label: 'Digitale Sprechstunde', icon: <EventIcon /> },
    { value: 'podcast', label: 'Podcast', icon: <EventIcon /> },
    { value: 'videocast', label: 'Videocast', icon: <EventIcon /> },
    { value: 'sonstige', label: 'Sonstige', icon: <EventIcon /> },
  ];

  const targetAudiences = [
    { value: 'kinder_jugendliche', label: 'Kinder & Jugendliche' },
    { value: 'bevoelkerung', label: 'Bevölkerung' },
    { value: 'unternehmen', label: 'Unternehmen' },
    { value: 'behoerden', label: 'Behörden' },
    { value: 'militaer', label: 'Militär' },
    { value: 'keine_angabe', label: 'Keine Angabe' },
  ];

  const daysOfWeek = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

  useEffect(() => {
    fetchEvents();
    fetchAttendanceStats();
    
    // Fetch CSRF token
    const fetchCsrfToken = async () => {
      const token = await getCsrfToken();
      setCsrfToken(token);
    };
    fetchCsrfToken();
  }, [currentWeek]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const weekDates = getWeekDates();
      // Fix: Use start of day (00:00:00) instead of current time
      const startDateObj = new Date(weekDates[0]);
      startDateObj.setHours(0, 0, 0, 0);
      const startDate = startDateObj.toISOString();
      
      const endDate = new Date(weekDates[6]);
      endDate.setHours(23, 59, 59, 999);
      const endDateISO = endDate.toISOString();
      
      
      const response = await axiosInstance.get(`/calendar/events?start_date=${startDate}&end_date=${endDateISO}`);
      
      setEvents(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceStats = async () => {
    try {
      setStatsLoading(true);
      const weekDates = getWeekDates();
      const weekStart = weekDates[0].toISOString();
      const response = await axiosInstance.get(`/calendar/events/attendance/stats?limit=3&week_start=${weekStart}`);
      setAttendanceStats(response.data);
    } catch (err) {
      console.error('Error fetching attendance stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const getWeekDates = () => {
    const startOfWeek = new Date(currentWeek);
    // Get Monday as start of week (1 = Monday, 0 = Sunday)
    const dayOfWeek = currentWeek.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday becomes 6 days back
    startOfWeek.setDate(currentWeek.getDate() - daysToSubtract);
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const getCalendarWeek = (date) => {
    // Create a new date object to avoid mutating the original
    const target = new Date(date.valueOf());
    
    // ISO week date weeks start on Monday, so correct the day number
    // Monday = 0, Tuesday = 1, ..., Sunday = 6
    const dayNr = (date.getDay() + 6) % 7;
    
    // Set the target to the Thursday of the target week
    target.setDate(target.getDate() - dayNr + 3);
    
    // Get the first Thursday of the year
    const firstThursday = new Date(target.getFullYear(), 0, 1);
    if (firstThursday.getDay() !== 4) {
      firstThursday.setMonth(0, 1 + ((4 - firstThursday.getDay() + 7) % 7));
    }
    
    // If the target date is before the first Thursday of the year,
    // it belongs to the last week of the previous year
    if (target < firstThursday) {
      const lastDayOfPrevYear = new Date(target.getFullYear() - 1, 11, 31);
      return getCalendarWeek(lastDayOfPrevYear);
    }
    
    // Calculate the week number
    const weekDiff = (target.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000);
    return 1 + Math.floor(weekDiff);
  };

  const formatWeekDisplay = () => {
    const weekDates = getWeekDates();
    const startDate = weekDates[0];
    const endDate = weekDates[6];
    const calendarWeek = getCalendarWeek(startDate);
    
    const formatDate = (date) => {
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    
    return `KW ${calendarWeek} - ${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      // Normalize the calendar date to start of day for comparison
      const calendarDate = new Date(date);
      calendarDate.setHours(0, 0, 0, 0);
      
      // Get event start and end dates
      const eventStartDate = new Date(event.start_date);
      eventStartDate.setHours(0, 0, 0, 0);
      
      const eventEndDate = event.end_date ? new Date(event.end_date) : eventStartDate;
      eventEndDate.setHours(0, 0, 0, 0);
      
      // Check if calendar date falls within the event date range (inclusive)
      return calendarDate >= eventStartDate && calendarDate <= eventEndDate;
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      cybersecurity: '#1976d2',
      conference: '#388e3c',
      training: '#f57c00',
      vulnerability: '#d32f2f',
      alert: '#c62828',
    };
    return colors[eventType] || '#757575';
  };

  const getEventTypeIcon = (eventType) => {
    const type = eventTypes.find(t => t.value === eventType);
    return type ? type.icon : <EventIcon />;
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      const startDate = new Date(event.start_date);
      const endDate = event.end_date ? new Date(event.end_date) : startDate;
      
      // Debug: Log the event data being loaded
      console.log('Loading event for editing:', event);
      
      setFormData({
        title: event.title,
        description: event.description || '',
        start_date: startDate.toISOString().split('T')[0],
        start_time: startDate.toTimeString().slice(0, 5),
        end_date: endDate.toISOString().split('T')[0],
        end_time: endDate.toTimeString().slice(0, 5),
        event_type: event.event_type,
        target_audience: event.target_audience || 'bevoelkerung',
        location: event.location || '',
        url: event.url || '',
        organizer: event.organizer || '',
        organizer_logo: event.organizer_logo || '',
        show_logo: event.show_logo === true, // Default to false if not set
        is_all_day: event.is_all_day,
      });
      setLogoFile(null);
      setLogoPreview(null);
    } else {
      setEditingEvent(null);
      const today = new Date();
      setFormData({
        title: '',
        description: '',
        start_date: today.toISOString().split('T')[0],
        start_time: '09:00',
        end_date: today.toISOString().split('T')[0],
        end_time: '10:00',
        event_type: 'cybersecurity',
        target_audience: 'bevoelkerung',
        location: '',
        url: '',
        organizer: '',
        organizer_logo: '',
        show_logo: false,
        is_all_day: false,
      });
      setLogoFile(null);
      setLogoPreview(null);
    }
    setOpenDialog(true);
  };

  // Security validation functions
  const validateUrl = (url) => {
    if (!url || url.trim() === '') return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Image upload functions
  const handleLogoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrfToken
        }
      });
      
      return response.data.image_id;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const handleFaviconExtraction = async (url) => {
    try {
      const response = await axiosInstance.post('/images/favicon', { url }, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      return response.data.image_id;
    } catch (error) {
      console.error('Error extracting favicon:', error);
      return null;
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const sanitizeDescription = (desc) => {
    if (!desc) return desc;
    // Remove script tags and other potentially dangerous HTML
    return desc.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
               .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
               .replace(/javascript:/gi, '')
               .replace(/on\w+\s*=/gi, '');
  };

  // Function to get CSRF token
  const getCsrfToken = async () => {
    try {
      const response = await fetch('/api/v1/csrf-token');
      const data = await response.json();
      return data.csrf_token;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return '';
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      event_type: 'cybersecurity',
      location: '',
      url: '',
      organizer: '',
      organizer_logo: '',
      is_all_day: false,
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSubmit = async () => {
    try {
      // Validate URL if provided
      if (formData.url && !validateUrl(formData.url)) {
        setError('Please enter a valid URL');
        return;
      }

      // Validate date range
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);
      
      if (endDateTime <= startDateTime) {
        setError('End date must be after start date');
        return;
      }

      // Sanitize description
      const sanitizedDescription = sanitizeDescription(formData.description);
      
      // Handle logo upload
      let logoImageId = null;
      if (logoFile) {
        // New file uploaded - always use the new file
        logoImageId = await handleLogoUpload(logoFile);
      } else if (editingEvent && formData.organizer_logo && !logoFile && formData.organizer_logo !== '') {
        // Keep existing logo when editing (only if no new file selected and logo not explicitly removed)
        logoImageId = formData.organizer_logo;
      } else if (formData.url && !logoFile && !editingEvent) {
        // Only try to extract favicon for new events
        logoImageId = await handleFaviconExtraction(formData.url);
      }
      // If logoImageId is null, it will be set to null in the database (removing the logo)
      
      const eventData = {
        title: formData.title.trim(),
        description: sanitizedDescription,
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
        event_type: formData.event_type,
        target_audience: formData.target_audience,
        location: formData.location?.trim() || null,
        url: formData.url?.trim() || null,
        organizer: formData.organizer?.trim() || null,
        organizer_logo: logoImageId,
        show_logo: formData.show_logo,
        is_all_day: formData.is_all_day,
      };

      // Debug: Log the event data being sent
      console.log('Sending event data:', eventData);

      if (editingEvent) {
        await axiosInstance.put(`/calendar/events/${editingEvent.id}`, eventData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axiosInstance.post('/calendar/events', eventData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }

      handleCloseDialog();
      fetchEvents();
      fetchAttendanceStats(); // Refresh statistics when events are created/updated
      setError(null); // Clear any previous errors
      setSuccess(editingEvent ? 'Event erfolgreich aktualisiert!' : 'Event erfolgreich erstellt!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('calendarEventsUpdated'));
    } catch (err) {
      console.error('Error saving event:', err);
      setError('Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    setDeleteDialog({ open: true, eventId });
  };

  const confirmDeleteEvent = async () => {
    if (!deleteDialog.eventId) return;
    
    try {
      await axiosInstance.delete(`/calendar/events/${deleteDialog.eventId}`, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      fetchEvents();
      setDeleteDialog({ open: false, eventId: null });
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('calendarEventsUpdated'));
    } catch (err) {
      console.error('Error deleting event:', err);
      setError('Failed to delete event');
    }
  };

  const handleAttendEvent = async (eventId) => {
    try {
      await axiosInstance.post(`/calendar/events/${eventId}/attend`, {}, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      fetchEvents(); // Refresh events to update attendance
      fetchAttendanceStats(); // Refresh statistics
    } catch (err) {
      console.error('Error attending event:', err);
      setError('Failed to mark attendance');
    }
  };

  const handleUnattendEvent = async (eventId) => {
    try {
      await axiosInstance.delete(`/calendar/events/${eventId}/attend`, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      fetchEvents(); // Refresh events to update attendance
      fetchAttendanceStats(); // Refresh statistics
    } catch (err) {
      console.error('Error removing attendance:', err);
      setError('Failed to remove attendance');
    }
  };

  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(currentWeek.getDate() + (direction * 7));
    setCurrentWeek(newWeek);
  };

  // Chart configurations
  const getAttendanceBarChartOption = () => {
    const chartData = attendanceStats.map(stat => ({
      name: stat.title.length > 20 ? stat.title.substring(0, 20) + '...' : stat.title,
      value: stat.attendee_count,
      fullName: stat.title
    }));

    return {
      title: {
        text: 'Top 3 Events nach Teilnehmerzahl',
        left: 'center',
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 14,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          const data = params[0];
          const stat = attendanceStats[data.dataIndex];
          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 4px;">${stat.title}</div>
              <div>Teilnehmer: ${stat.attendee_count}</div>
              <div>Datum: ${new Date(stat.start_date).toLocaleDateString('de-DE')}</div>
              ${stat.location ? `<div>Ort: ${stat.location}</div>` : ''}
            </div>
          `;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: chartData.map(item => item.name),
        axisLabel: {
          color: theme.palette.text.primary,
          rotate: 45
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.divider
          }
        }
      },
      yAxis: {
        type: 'value',
        name: 'Teilnehmer',
        axisLabel: {
          color: theme.palette.text.primary
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.divider
          }
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider
          }
        }
      },
      series: [
        {
          name: 'Teilnehmer',
          type: 'bar',
          data: chartData.map(item => item.value),
          itemStyle: {
            color: theme.palette.primary.main,
            borderRadius: [4, 4, 0, 0]
          },
          emphasis: {
            itemStyle: {
              color: theme.palette.primary.dark
            }
          }
        }
      ]
    };
  };

  const getAttendancePieChartOption = () => {
    const chartData = attendanceStats.map((stat, index) => ({
      name: stat.title.length > 15 ? stat.title.substring(0, 15) + '...' : stat.title,
      value: stat.attendee_count,
      fullName: stat.title
    }));

    const colors = ['#1976d2', '#388e3c', '#f57c00'];

    return {
      title: {
        text: 'Verteilung der Teilnehmer',
        left: 'center',
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 14,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          const stat = attendanceStats[params.dataIndex];
          const percentage = ((params.value / attendanceStats.reduce((sum, s) => sum + s.attendee_count, 0)) * 100).toFixed(1);
          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 4px;">${stat.title}</div>
              <div>Teilnehmer: ${stat.attendee_count} (${percentage}%)</div>
              <div>Datum: ${new Date(stat.start_date).toLocaleDateString('de-DE')}</div>
            </div>
          `;
        }
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: {
          color: theme.palette.text.primary
        }
      },
      series: [
        {
          name: 'Teilnehmer',
          type: 'pie',
          radius: '50%',
          data: chartData.map((item, index) => ({
            ...item,
            itemStyle: {
              color: colors[index % colors.length]
            }
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please log in to access the Security Event Calendar.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Security Event Kalender
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: 'black', '&:hover': { bgcolor: 'darkgray' } }}
        >
          Event hinzufügen
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Week Display */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Typography variant="h6">
          {formatWeekDisplay()}
        </Typography>
      </Box>
      
      {/* Week Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button onClick={() => navigateWeek(-1)}>Vorherige Woche</Button>
        <Button 
          variant="outlined" 
          onClick={() => setCurrentWeek(new Date())}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          Heute
        </Button>
        <Button onClick={() => navigateWeek(1)}>Nächste Woche</Button>
      </Box>

      {/* Attendance Statistics Panels */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ mr: 1 }} />
          Event Statistiken
        </Typography>
        
        {statsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={40} />
          </Box>
        ) : attendanceStats.length > 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <ReactECharts 
                    option={getAttendanceBarChartOption()} 
                    style={{ height: '300px' }}
                    opts={{ renderer: 'canvas' }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <ReactECharts 
                    option={getAttendancePieChartOption()} 
                    style={{ height: '300px' }}
                    opts={{ renderer: 'canvas' }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="info">
            Keine Event-Daten verfügbar. Erstellen Sie Events und markieren Sie Teilnahme, um Statistiken zu sehen.
          </Alert>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {getWeekDates().map((date, index) => (
            <Grid item xs={12} sm={6} md={12/7} key={index}>
              <Card 
                elevation={2}
                sx={{
                  height: '100%',
                  minHeight: 200,
                  bgcolor: date.toDateString() === new Date().toDateString() ? 'primary.light' : 'background.paper',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: date.toDateString() === new Date().toDateString() ? 'white' : 'text.primary',
                      mb: 1
                    }}
                  >
                    {daysOfWeek[index]}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: date.toDateString() === new Date().toDateString() ? 'white' : 'text.secondary',
                      mb: 2,
                      display: 'block'
                    }}
                  >
                    {date.toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </Typography>
                  
                  <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                    {getEventsForDate(date).map((event) => (
                      <Paper
                        key={event.id}
                        sx={{
                          p: 1,
                          mb: 1,
                          bgcolor: getEventTypeColor(event.event_type),
                          color: 'white',
                          position: 'relative',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                              {event.title}
                            </Typography>
                            {!event.is_all_day && (
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {formatTime(event.start_date)}
                              </Typography>
                            )}
                            {event.location && (
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                <LocationIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                {event.location}
                              </Typography>
                            )}
                            {event.user && (
                              <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                                Created by: {event.user.first_name} {event.user.last_name}
                              </Typography>
                            )}
                            {/* Attendance information */}
                            {event.attendee_count > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                <PeopleIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                {event.attendee_count} {event.attendee_count === 1 ? 'member' : 'members'} of our community will be there
                              </Typography>
                            )}
                            {/* Attendance button */}
                            <Box sx={{ mt: 1 }}>
                              {event.is_attending ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CancelIcon />}
                                  onClick={() => handleUnattendEvent(event.id)}
                                  sx={{ 
                                    color: 'white', 
                                    borderColor: 'white',
                                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                                  }}
                                >
                                  Nicht teilnehmen
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleAttendEvent(event.id)}
                                  sx={{ 
                                    color: 'white', 
                                    borderColor: 'white',
                                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                                  }}
                                >
                                  Teilnehmen
                                </Button>
                              )}
                            </Box>
                          </Box>
                          <Box>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(event)}
                                sx={{ color: 'white', p: 0.5 }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteEvent(event.id)}
                                sx={{ color: 'white', p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Event Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Event bearbeiten' : 'Neues Event hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Titel"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Event Typ</InputLabel>
                <Select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  label="Event Typ"
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {type.icon}
                        <Typography sx={{ ml: 1 }}>{type.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Zielgruppe</InputLabel>
                <Select
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  label="Zielgruppe"
                >
                  {targetAudiences.map((audience) => (
                    <MenuItem key={audience.value} value={audience.value}>
                      {audience.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_all_day}
                    onChange={(e) => setFormData({ ...formData, is_all_day: e.target.checked })}
                  />
                }
                label="All Day Event (ganztägig)"
              />
            </Grid>

            {!formData.is_all_day && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Startdatum"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Startzeit"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Enddatum"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Endzeit"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            {formData.is_all_day && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Startdatum"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Enddatum"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ort"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Veranstalter/Organisator"
                value={formData.organizer}
                onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                placeholder="z.B. Cyberlagebild Community, BSI, etc."
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Organisator Logo
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ marginBottom: '8px' }}
                />
                {logoPreview && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      style={{ 
                        maxWidth: '100px', 
                        maxHeight: '100px', 
                        objectFit: 'contain',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }} 
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                        setFormData(prev => ({ ...prev, organizer_logo: '' }));
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  Laden Sie ein Logo hoch oder lassen Sie es leer, um automatisch das Favicon der Website zu verwenden
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.show_logo}
                    onChange={(e) => setFormData({ ...formData, show_logo: e.target.checked })}
                    color="primary"
                  />
                }
                label="Logo in der Veranstaltungsübersicht anzeigen"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.title}
          >
            {editingEvent ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, eventId: null })}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Event löschen bestätigen
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie dieses Event löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, eventId: null })}
            color="primary"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={confirmDeleteEvent}
            color="error"
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecEventCalendar;
